// Bridge between the portal's legacy trackEvent() stream and the shared Guru
// user-action recorder (gurucloudai kanban b91c17bf; ChemMasters cards
// 142159ee, 964045bb).
//
// Two sources feed the recorder:
//   1. The library's own delegated DOM listener records every click/change/
//      search on a control, named by its data-action (else role + accessible
//      name) with data-target-* ids and chosen values.
//   2. trackEvent(type, target, metadata) calls, which keep posting to
//      /api/portal/events/batch unchanged AND are forwarded here as
//      GuruRecorder.record(<target>, {targets, values, outcome}).
//
// A control that carries data-action="<legacy target>" is recorded by (1);
// when its click handler then also calls trackEvent with the same target
// within DEDUPE_WINDOW_MS, the forward is skipped so the row is not doubled.
//
// Mapping of trackEvent metadata keys (everything else stays in
// portal_events only):
//   targets <- keys that look like ids: *_id, *_no, *_ref, *_code, id, slug,
//              quote_ref, slot
//   values  <- chosen values only: query, tab, filter, category, sort_by,
//              state, salesperson, regional_responsible, status, order_status,
//              scope, field (a field NAME, never its value), mode, view, and
//              the counts count/requested/dismissed/skipped/overlapping_items
//   outcome <- metadata.outcome when it is a recorder outcome
//              (navigate|request|open|close|none)
// Free text (message bodies, notes, prices typed) is never forwarded.
//
// A trackEvent call whose control is ALREADY recorded by the library through
// a path the click/change dedupe cannot see (a search input's debounced
// `input` row, a row written after an async request) passes
// `recorded_by_dom: true` in its metadata: the forward is skipped and the key
// is stripped before the portal_events row is queued (kanban 68b23ba8, the
// orders search box used to land twice: portal_orders_search then
// search_search_orders 160 ms later).

import type { PortalEventType } from './portalTracking';
import type {
  GuruRecorderOutcome,
  GuruRecorderRecordOptions,
  GuruRecorderUser,
} from '../types/guru-recorder';
import { getGuruRecorder, onGuruRecorderReady } from '../utils/guruRecorderLoader';
import { resolvePortalScreen } from './portalScreens';

const OUTCOMES: readonly GuruRecorderOutcome[] = ['navigate', 'request', 'open', 'close', 'none'];
const DEDUPE_WINDOW_MS = 1000;

/** Metadata key a trackEvent caller sets when the annotated DOM control already records the row. */
export const RECORDED_BY_DOM_KEY = 'recorded_by_dom';

const TARGET_KEYS = new Set(['id', 'slug', 'slot']);
const TARGET_SUFFIXES = ['_id', '_no', '_ref', '_code'];
const VALUE_KEYS = new Set([
  'query',
  'tab',
  'filter',
  'category',
  'sort_by',
  'state',
  'salesperson',
  'regional_responsible',
  'status',
  'order_status',
  'scope',
  'field',
  'mode',
  'view',
  'count',
  'requested',
  'dismissed',
  'skipped',
  'overlapping_items',
]);

export interface MappedRecorderEvent {
  action: string;
  targets: Record<string, string>;
  values: Record<string, string>;
  outcome?: GuruRecorderOutcome;
}

function isTargetKey(key: string): boolean {
  if (TARGET_KEYS.has(key)) return true;
  return TARGET_SUFFIXES.some((suffix) => key.endsWith(suffix));
}

function scalar(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

/**
 * Pure mapping of a legacy trackEvent call onto a recorder row. Returns null
 * for navigation events (those become setScreen, see forwardTrackEvent).
 */
export function mapTrackEvent(
  eventType: PortalEventType,
  eventTarget: string,
  metadata?: Record<string, unknown> | null,
): MappedRecorderEvent | null {
  if (eventType === 'navigation' || eventTarget === 'page_view') return null;
  if (metadata && metadata[RECORDED_BY_DOM_KEY]) return null;
  const targets: Record<string, string> = {};
  const values: Record<string, string> = {};
  let outcome: GuruRecorderOutcome | undefined;
  for (const [key, raw] of Object.entries(metadata ?? {})) {
    const value = scalar(raw);
    if (value === null) continue;
    if (key === 'outcome') {
      if ((OUTCOMES as readonly string[]).includes(value)) outcome = value as GuruRecorderOutcome;
      continue;
    }
    if (key === RECORDED_BY_DOM_KEY) continue;
    if (isTargetKey(key)) targets[key] = value;
    else if (VALUE_KEYS.has(key)) values[key] = value;
  }
  return { action: eventTarget, targets, values, ...(outcome ? { outcome } : {}) };
}

// ─── DOM dedupe ─────────────────────────────────────────────────────────────

let _lastDomAction: { name: string; at: number } | null = null;
let _domListening = false;

function noteDomAction(ev: Event): void {
  const target = ev.target as Element | null;
  const el = target && typeof target.closest === 'function' ? target.closest('[data-action]') : null;
  const name = el?.getAttribute('data-action');
  if (name) _lastDomAction = { name, at: Date.now() };
}

/** Watch DOM clicks/changes so forwarded trackEvent rows can be deduped. */
export function startRecorderDomDedupe(): void {
  if (_domListening || typeof document === 'undefined') return;
  _domListening = true;
  document.addEventListener('click', noteDomAction, true);
  document.addEventListener('change', noteDomAction, true);
}

export function stopRecorderDomDedupe(): void {
  if (!_domListening) return;
  _domListening = false;
  document.removeEventListener('click', noteDomAction, true);
  document.removeEventListener('change', noteDomAction, true);
  _lastDomAction = null;
}

function recordedByDom(action: string): boolean {
  return (
    _lastDomAction !== null &&
    _lastDomAction.name === action &&
    Date.now() - _lastDomAction.at <= DEDUPE_WINDOW_MS
  );
}

// ─── Forwarding ─────────────────────────────────────────────────────────────

/**
 * Forward one legacy trackEvent call to the recorder. Navigation events set
 * the screen (route template + path); everything else becomes a record().
 * No-op when the library is not loaded; never throws.
 */
export function forwardTrackEvent(
  eventType: PortalEventType,
  eventTarget: string,
  metadata?: Record<string, unknown> | null,
): void {
  try {
    const api = getGuruRecorder();
    if (!api) return;
    if (eventType === 'navigation' || eventTarget === 'page_view') {
      const path = scalar(metadata?.path) ?? window.location.pathname;
      api.setScreen(resolvePortalScreen(path), path);
      return;
    }
    const mapped = mapTrackEvent(eventType, eventTarget, metadata);
    if (!mapped || recordedByDom(mapped.action)) return;
    const opts: GuruRecorderRecordOptions = { targets: mapped.targets, values: mapped.values };
    if (mapped.outcome) opts.outcome = mapped.outcome;
    api.record(mapped.action, opts);
  } catch {
    // recording must never break the page
  }
}

/** Record an explicit row (for actions with no DOM control, e.g. a saved result). */
export function recordAction(action: string, opts?: GuruRecorderRecordOptions): void {
  try {
    getGuruRecorder()?.record(action, opts);
  } catch {
    // never break the page
  }
}

// Screen and user set before the script has loaded are kept (latest wins) and
// applied once, so screens passed through while loading do not each emit a
// page_view/page_leave pair.
let _pendingScreen: { template: string; path: string } | null = null;
let _pendingUser: GuruRecorderUser | null | undefined;
let _unsubscribeReady: (() => void) | null = null;

function applyPending(): void {
  const api = getGuruRecorder();
  if (!api) return;
  try {
    if (_pendingUser !== undefined) api.setUser(_pendingUser);
    if (_pendingScreen) api.setScreen(_pendingScreen.template, _pendingScreen.path);
  } catch {
    // never break the page
  }
  _pendingScreen = null;
  _pendingUser = undefined;
}

function whenReady(): void {
  if (getGuruRecorder()) {
    applyPending();
    return;
  }
  if (!_unsubscribeReady) {
    _unsubscribeReady = onGuruRecorderReady(() => {
      _unsubscribeReady = null;
      applyPending();
    });
  }
}

/** Set the current screen (route template + concrete path). */
export function setRecorderScreen(template: string, path: string): void {
  _pendingScreen = { template, path };
  whenReady();
}

/** Identify the person (stable id, never an email). */
export function setRecorderUser(user: GuruRecorderUser | null): void {
  _pendingUser = user;
  whenReady();
}

/** Test-only. */
export function _resetRecorderBridgeForTests(): void {
  stopRecorderDomDedupe();
  _pendingScreen = null;
  _pendingUser = undefined;
  if (_unsubscribeReady) _unsubscribeReady();
  _unsubscribeReady = null;
}
